import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiFetch } from "./apiClient";

import type { Page } from "./page";

// Re-export kvůli zpětné kompatibilitě importů `from "@/lib/transactionLogs"`.
export type { Page };

export interface TransactionLogEntry {
  id: number;
  operationType: "APPROVE" | "REJECT" | string;
  success: boolean;
  tokenId: string;
  username: string | null;
  authServerType: string;
  transactionId: string | null;
  durationMs: number | null;
  createdAt: string;
}

export interface TransactionLogDetail extends TransactionLogEntry {
  authServerTransactionId: string | null;
  paatProfileId: string | null;
  wysiwygData: string | null;
  encryptedDataHash: string | null;
  decryptedData: Record<string, unknown> | null;
  signature: string | null;
  signatureSource: string | null;
  passwordProvided: boolean | null;
  passwordSource: string | null;
  mobileSdkData: Record<string, unknown> | null;
  deviceAuthType: number | null;
  secureChannelMessageSignatureHash: string | null;
  rejectionReason: string | null;
  errorMessage: string | null;
  errorCode: string | null;
  traceId: string | null;
  operationStartedAt: string | null;
  operationCompletedAt: string | null;
  requestIp?: string | null;
  userAgent?: string | null;
  requestCorrelationId?: string | null;
}

export interface TransactionLogStats {
  totalOperations: number;
  successfulOperations: number;
  failedOperations: number;
  approveOperations: number;
  rejectOperations: number;
  operationsLast24h: number;
  averageDurationMs: number;
  successRate: number;
}

export interface TransactionLogFilter {
  tokenId?: string;
  operationType?: string;
  success?: string;
  authServerType?: string;
  fromDate?: string;
  toDate?: string;
}

const BASE = "/admin/api/v1/transaction-logs";

function filterQs(filter: TransactionLogFilter): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v != null && String(v).trim() !== "") p.set(k, String(v).trim());
  }
  return p.toString();
}

/** Stránkovaný + filtrovaný seznam transakčních logů (+volitelný auto-refresh). */
export function useTransactionLogs(page: number, size: number, filter: TransactionLogFilter = {}, refetchIntervalMs = 0) {
  const qs = filterQs(filter);
  return useQuery({
    queryKey: ["transaction-logs", page, size, qs],
    queryFn: () => apiFetch<Page<TransactionLogEntry>>(`${BASE}?page=${page}&size=${size}${qs ? `&${qs}` : ""}`),
    placeholderData: keepPreviousData,
    // Log je proud, ne stav — globální staleTime 30 s by po návratu na stránku vracel
    // půl minuty starý snímek bez dotazu na server (refetchOnWindowFocus je vypnutý).
    staleTime: 0,
    refetchInterval: refetchIntervalMs > 0 ? refetchIntervalMs : false,
  });
}

export function useTransactionLogStats(refetchIntervalMs = 0) {
  return useQuery({
    queryKey: ["transaction-logs", "stats"],
    queryFn: () => apiFetch<TransactionLogStats>(`${BASE}/stats`),
    staleTime: 0,
    refetchInterval: refetchIntervalMs > 0 ? refetchIntervalMs : false,
  });
}

export function useTransactionLog(id: number | null) {
  return useQuery({
    queryKey: ["transaction-logs", "detail", id],
    queryFn: () => apiFetch<TransactionLogDetail>(`${BASE}/${id}`),
    enabled: id != null,
  });
}
